/**
 * Clean Shave Comparison Component
 * 实现前后对比图片的交互功能
 */
if (!customElements.get('clean-shave-component')) {
  class CleanShaveComponent extends HTMLElement {
    constructor() {
      super();
      this.position = parseInt(this.dataset.initialPosition) || 50;
      this.isAutoPlaying = this.dataset.autoPlay === 'true';
      this.autoPlaySpeed = parseInt(this.dataset.autoPlaySpeed) || 4;
      this.autoPlayDirection = 1;
      this.autoPlayInterval = null;
      this.isAnimating = false;
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      // 在拖拽结束后的短时间内屏蔽按钮 click，防止“拖完即跳”
      this._suppressButtonClick = false;
    }

    connectedCallback() {
      this.setupElements();
      this.setupEventListeners();
      this.setupVideoModal();
      if (this.animations_enabled) {
        this.setupGSAPAnimations();
      }
      this.updatePosition(this.position);
      
      if (this.isAutoPlaying) {
        this.startAutoPlay();
      }
      
      // 设置初始可访问性
      this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'img');
      this.setAttribute('aria-label', '剃须前后对比图片');
    }

    disconnectedCallback() {
      this.stopAutoPlay();
      if (this.observer) {
        this.observer.disconnect();
      }
      this.cleanupVideoModal();
    }

    setupElements() {
      this.slider = this.querySelector('.clean-shave-comparison__slider');
      this.button = this.querySelector('.clean-shave-comparison__button');
      this.divider = this.querySelector('.clean-shave-comparison__divider');
      this.afterImage = this.querySelector('.clean-shave-comparison__image--after');
      this.component = this.querySelector('.clean-shave-comparison__images');
      this.controls = this.querySelector('.clean-shave-comparison__controls');
    }

    setupEventListeners() {
      // 滑块拖拽
      if (this.slider) {
        let sliderAnimationFrame = null;
        let isSliderDragging = false;
        
        this.slider.addEventListener('mousedown', () => {
          isSliderDragging = true;
          this.disableTransitions();
        });
        
        this.slider.addEventListener('input', (e) => {
          this.position = parseInt(e.target.value);
          
          if (sliderAnimationFrame) {
            cancelAnimationFrame(sliderAnimationFrame);
          }
          
          sliderAnimationFrame = requestAnimationFrame(() => {
            if (isSliderDragging) {
              this.updatePositionDuringDrag(this.position);
            } else {
              this.updatePosition(this.position);
            }
          });
          
          this.pauseAutoPlay();
        });

        this.slider.addEventListener('change', (e) => {
          isSliderDragging = false;
          this.enableTransitions();
          this.announcePosition();
        });
        
        // 处理键盘操作结束
        this.slider.addEventListener('keyup', (e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            isSliderDragging = false;
            this.enableTransitions();
            this.announcePosition();
          }
        });
      }

      // 按钮点击切换
      if (this.button) {
        this.button.addEventListener('click', (e) => {
          // 若刚刚发生过有效拖拽，则忽略这次 click，避免位置被切到 0 或 100
          if (this._suppressButtonClick) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          e.preventDefault();
          this.togglePosition();
        });
      }

      // 键盘导航
      let keyboardAnimationFrame = null;
      let isKeyboardActive = false;
      
      this.addEventListener('keydown', (e) => {
        switch(e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            if (!isKeyboardActive) {
              isKeyboardActive = true;
              this.disableTransitions();
            }
            if (keyboardAnimationFrame) {
              cancelAnimationFrame(keyboardAnimationFrame);
            }
            keyboardAnimationFrame = requestAnimationFrame(() => {
              this.adjustPositionDuringKeyboard(-5);
            });
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (!isKeyboardActive) {
              isKeyboardActive = true;
              this.disableTransitions();
            }
            if (keyboardAnimationFrame) {
              cancelAnimationFrame(keyboardAnimationFrame);
            }
            keyboardAnimationFrame = requestAnimationFrame(() => {
              this.adjustPositionDuringKeyboard(5);
            });
            break;
          case ' ':
          case 'Enter':
            e.preventDefault();
            this.togglePosition();
            break;
        }
      });
      
      this.addEventListener('keyup', (e) => {
        if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && isKeyboardActive) {
          isKeyboardActive = false;
          this.enableTransitions();
          this.announcePosition();
        }
      });

      // 触摸事件
      this.setupTouchEvents();
      
      // IntersectionObserver for entrance animation
      this.setupIntersectionObserver();

      // 鼠标悬停暂停自动播放
      this.addEventListener('mouseenter', () => {
        this.pauseAutoPlay();
      });

      this.addEventListener('mouseleave', () => {
        if (this.isAutoPlaying) {
          setTimeout(() => {
            this.startAutoPlay();
          }, 2000);
        }
      });
    }

    setupGSAPAnimations() {
      if (this.animations_enabled && this.component) {
        this.entranceTl = gsap.timeline({ paused: true });
        this.entranceTl
          .from(this.component, {
            scale: 0.8,
            opacity: 0,
            duration: 0.8,
            ease: "power2.out"
          })
          .from(this.button, {
            scale: 0,
            rotation: 180,
            duration: 0.5,
            ease: "back.out(1.7)"
          }, "-=0.3");
      }
    }

    updatePosition(newPosition) {
      const clampedPosition = Math.max(0, Math.min(100, newPosition));
      this.style.setProperty('--comparison-position', `${clampedPosition}%`);
      
      if (this.slider) {
        this.slider.value = clampedPosition;
      }
      
      this.position = clampedPosition;
      
      // 触觉反馈
      if ('vibrate' in navigator && Math.abs(this.position - clampedPosition) > 0) {
        navigator.vibrate(10);
      }
    }

    updatePositionDuringDrag(newPosition) {
      const clampedPosition = Math.max(0, Math.min(100, newPosition));
      this.style.setProperty('--comparison-position', `${clampedPosition}%`);
      
      if (this.slider) {
        this.slider.value = clampedPosition;
      }
      
      this.position = clampedPosition;
    }

    disableTransitions() {
      if (this.button) {
        this.button.style.transition = 'none';
      }
      
      if (this.divider) {
        this.divider.style.transition = 'none';
      }
      
      if (this.afterImage) {
        this.afterImage.style.transition = 'none';
      }
    }

    enableTransitions() {
      if (this.button) {
        this.button.style.transition = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      }
      
      if (this.divider) {
        this.divider.style.transition = 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      }
      
      if (this.afterImage) {
        this.afterImage.style.transition = 'clip-path 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      }
    }

    togglePosition() {
      if (this.isAnimating) return;
      
      this.isAnimating = true;
      const targetPosition = this.position > 50 ? 0 : 100;
      
      if (this.animations_enabled && typeof gsap !== 'undefined') {
        // 按钮旋转动画
        if (this.button) {
          gsap.to(this.button.querySelector('svg'), {
            rotation: targetPosition === 100 ? 0 : 180,
            duration: 0.3,
            ease: "power2.out"
          });
        }
        
        // 位置切换动画
        gsap.to(this, {
          position: targetPosition,
          duration: 0.8,
          ease: "power2.inOut",
          onUpdate: () => {
            this.updatePosition(this.position);
          },
          onComplete: () => {
            this.isAnimating = false;
            this.announcePosition();
          }
        });
      } else {
        this.updatePosition(targetPosition);
        this.isAnimating = false;
        this.announcePosition();
      }
      
      this.pauseAutoPlay();
    }

    adjustPosition(delta) {
      this.updatePosition(this.position + delta);
      this.pauseAutoPlay();
      this.announcePosition();
    }

    adjustPositionDuringKeyboard(delta) {
      this.updatePositionDuringDrag(this.position + delta);
      this.pauseAutoPlay();
    }

    startAutoPlay() {
      if (this.autoPlayInterval || !this.isAutoPlaying) return;
      
      this.autoPlayInterval = setInterval(() => {
        if (this.position >= 100) {
          this.autoPlayDirection = -1;
        } else if (this.position <= 0) {
          this.autoPlayDirection = 1;
        }
        
        this.updatePosition(this.position + (this.autoPlayDirection * 2));
      }, this.autoPlaySpeed * 20);
    }

    stopAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    }

    pauseAutoPlay() {
      this.stopAutoPlay();
      
      // 3秒后重新开始自动播放
      if (this.isAutoPlaying) {
        setTimeout(() => {
          this.startAutoPlay();
        }, 3000);
      }
    }

    setupTouchEvents() {
      if (!this.component) return;
      
      let startX = 0;
      let startPosition = 0;
      let isDragging = false;
      let animationFrame = null;
      let pendingUpdate = null;
      // 超过该阈值视为有效拖拽，随后会短暂屏蔽按钮 click
      const DRAG_THRESHOLD_PX = 3;
      
      const handleStart = (e) => {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        startX = clientX;
        startPosition = this.position;
        isDragging = true;
        // 重置“是否发生有效拖拽”标记
        this._dragMoved = false;
        this.pauseAutoPlay();
        
        // 禁用transition以获得即时响应
        this.disableTransitions();
        
        // 添加视觉反馈
        if (this.button) {
          this.button.style.transform = 'translate(-50%, -50%) scale(1.1)';
        }
      };

      const handleMove = (e) => {
        if (!isDragging) return;
        
        e.preventDefault();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const deltaX = clientX - startX;
        const containerWidth = this.component.offsetWidth;
        const deltaPercent = (deltaX / containerWidth) * 100;
        const newPosition = startPosition + deltaPercent;
        // 标记发生了有效拖拽
        if (Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
          this._dragMoved = true;
        }
        
        // 使用requestAnimationFrame优化性能
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        
        animationFrame = requestAnimationFrame(() => {
          this.updatePositionDuringDrag(newPosition);
        });
      };

      const handleEnd = () => {
        if (!isDragging) return;
        
        isDragging = false;
        // 若发生过有效拖拽，则在短时间内屏蔽按钮 click
        if (this._dragMoved) {
          this._suppressButtonClick = true;
          setTimeout(() => {
            this._suppressButtonClick = false;
          }, 200);
        }
        
        // 取消待处理的动画帧
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
          animationFrame = null;
        }
        
        // 重新启用transitions
        this.enableTransitions();
        
        this.announcePosition();
        
        // 恢复button样式
        if (this.button) {
          this.button.style.transform = 'translate(-50%, -50%) scale(1)';
        }
      };
      
      // 鼠标事件
      this.component.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('mouseleave', handleEnd);
      
      // 触摸事件
      this.component.addEventListener('touchstart', handleStart, { passive: true });
      this.component.addEventListener('touchmove', handleMove, { passive: false });
      this.component.addEventListener('touchend', handleEnd);
      this.component.addEventListener('touchcancel', handleEnd);
    }

    setupIntersectionObserver() {
      if (typeof IntersectionObserver === 'undefined') return;
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.entranceTl) {
            this.entranceTl.play();
            this.observer.unobserve(this);
          }
        });
      }, { 
        threshold: 0.3,
        rootMargin: '0px 0px -10% 0px'
      });
      
      this.observer.observe(this);
    }

    announcePosition() {
      // 为屏幕阅读器提供位置信息
      const percentage = Math.round(this.position);
      const announcement = `对比位置：${percentage}%，显示${percentage > 50 ? '更多剃须后效果' : '更多剃须前效果'}`;
      
      // 创建临时的aria-live区域
      const liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.position = 'absolute';
      liveRegion.style.left = '-10000px';
      liveRegion.style.width = '1px';
      liveRegion.style.height = '1px';
      liveRegion.style.overflow = 'hidden';
      
      document.body.appendChild(liveRegion);
      
      setTimeout(() => {
        liveRegion.textContent = announcement;
        setTimeout(() => {
          document.body.removeChild(liveRegion);
        }, 1000);
      }, 100);
    }

    setupVideoModal() {
      // 获取视频相关元素
      const root = this.closest('.clean-shave-comparison') || document;
      this.videoRoot = root;
      this.videoButton = root.querySelector('.clean-shave-comparison__floating-video-button');
      this.videoModal = root.querySelector('.clean-shave-comparison__video-modal');
      // 容器用于阻断冒泡，避免点击文案区域也触发拖拽
      this.videoButtonWrapper = root.querySelector('.clean-shave-comparison__floating-video-button__content');
      
      if (!this.videoButton || !this.videoModal) return;
      
      this.videoContainer = this.videoModal.querySelector('.clean-shave-comparison__video-container');
      this.videoPlayer = this.videoModal.querySelector('[id^="video-player-"]');
      this.closeButtons = this.videoModal.querySelectorAll('[data-close-modal]');
      
      // 视频按钮点击事件
      this.videoButton.addEventListener('click', this.openVideoModal.bind(this));
      // 阻止按下时冒泡到图片容器，避免触发拖拽
      this.videoButton.addEventListener('mousedown', (e) => { e.stopPropagation(); });
      this.videoButton.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
      if (this.videoButtonWrapper) {
        this.videoButtonWrapper.addEventListener('mousedown', (e) => { e.stopPropagation(); });
        this.videoButtonWrapper.addEventListener('touchstart', (e) => { e.stopPropagation(); }, { passive: true });
      }
      
      // 关闭按钮事件
      this.closeButtons.forEach(button => {
        button.addEventListener('click', this.closeVideoModal.bind(this));
      });
      
      // ESC键关闭
      this.handleEscKey = (e) => {
        if (e.key === 'Escape' && this.videoModal.classList.contains('active')) {
          this.closeVideoModal();
        }
      };
      document.addEventListener('keydown', this.handleEscKey);
      
      // 防止视频弹窗影响拖动
      this.videoModal.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      });
      
      this.videoModal.addEventListener('touchstart', (e) => {
        e.stopPropagation();
      });
    }

    openVideoModal() {
      if (!this.videoModal || !this.videoButton) return;
      
      const desktopUrl = this.videoButton.dataset.videoUrl || '';
      const mobileUrl = this.videoButton.dataset.videoUrlMobile || '';
      const isMobile = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
      const videoUrl = (isMobile && mobileUrl) ? mobileUrl : desktopUrl;
      if (!videoUrl) return;
      
      // 暂停自动播放
      this.pauseAutoPlay();
      
      // 创建视频内容
      this.createVideoContent(videoUrl);
      
      // 显示弹窗
      this.videoModal.classList.add('active');
      this.videoModal.setAttribute('aria-hidden', 'false');
      
      // 禁用body滚动
      document.body.style.overflow = 'hidden';
      
      // 聚焦到关闭按钮以便键盘导航
      const closeButton = this.videoModal.querySelector('.clean-shave-comparison__video-modal-close');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }
    }

    closeVideoModal() {
      if (!this.videoModal) return;
      
      // 隐藏弹窗
      this.videoModal.classList.remove('active');
      this.videoModal.setAttribute('aria-hidden', 'true');
      
      // 恢复body滚动
      document.body.style.overflow = '';
      
      // 清理视频内容
      setTimeout(() => {
        if (this.videoPlayer) {
          this.videoPlayer.innerHTML = '';
        }
      }, 300);
      
      // 重新聚焦到视频按钮
      if (this.videoButton) {
        this.videoButton.focus();
      }
    }

    createVideoContent(url) {
      if (!this.videoPlayer) return;
      
      // 清空现有内容
      this.videoPlayer.innerHTML = '';
      
      // 检测视频类型并创建相应的播放器
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        this.createYouTubePlayer(url);
      } else if (url.includes('vimeo.com')) {
        this.createVimeoPlayer(url);
      } else {
        this.createNativeVideoPlayer(url);
      }
    }

    createYouTubePlayer(url) {
      const videoId = this.extractYouTubeId(url);
      if (!videoId) return;
      
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      
      this.videoPlayer.appendChild(iframe);
    }

    createVimeoPlayer(url) {
      const videoId = this.extractVimeoId(url);
      if (!videoId) return;
      
      const iframe = document.createElement('iframe');
      iframe.src = `https://player.vimeo.com/video/${videoId}?autoplay=1`;
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      
      this.videoPlayer.appendChild(iframe);
    }

    createNativeVideoPlayer(url) {
      const video = document.createElement('video');
      video.src = url;
      video.setAttribute('controls', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      video.style.width = '100%';
      video.style.height = '100%';
      
      this.videoPlayer.appendChild(video);
    }

    extractYouTubeId(url) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return (match && match[2].length === 11) ? match[2] : null;
    }

    extractVimeoId(url) {
      const regExp = /vimeo.com\/(?:.*#|.*\/videos\/|.*\/|)([0-9]+)/;
      const match = url.match(regExp);
      return match ? match[1] : null;
    }

    cleanupVideoModal() {
      if (this.handleEscKey) {
        document.removeEventListener('keydown', this.handleEscKey);
      }
      
      // 恢复body样式
      document.body.style.overflow = '';
    }

    // 公共方法供外部调用
    setPosition(newPosition) {
      this.updatePosition(newPosition);
    }

    getPosition() {
      return this.position;
    }

    enableAutoPlay() {
      this.isAutoPlaying = true;
      this.startAutoPlay();
    }

    disableAutoPlay() {
      this.isAutoPlaying = false;
      this.stopAutoPlay();
    }
  }

  customElements.define('clean-shave-component', CleanShaveComponent);
}

// 为传统浏览器提供降级支持
document.addEventListener('DOMContentLoaded', function() {
  // 检查是否支持自定义元素
  if (!window.customElements) {
    const components = document.querySelectorAll('.clean-shave-comparison__component');
    
    components.forEach(component => {
      const slider = component.querySelector('.clean-shave-comparison__slider');
      const button = component.querySelector('.clean-shave-comparison__button');
      
      if (slider) {
        slider.addEventListener('input', function(e) {
          const position = e.target.value;
          component.style.setProperty('--comparison-position', `${position}%`);
        });
      }
      
      if (button) {
        let currentPosition = 50;
        button.addEventListener('click', function() {
          currentPosition = currentPosition > 50 ? 0 : 100;
          component.style.setProperty('--comparison-position', `${currentPosition}%`);
          if (slider) {
            slider.value = currentPosition;
          }
        });
      }
    });
  }
});

// 为开发环境提供调试信息
if (window.location.hostname.includes('ngrok') || window.location.hostname.includes('localhost')) {
  console.log('Clean Shave Comparison component loaded successfully');
}
